import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

/**
 * RNF17 - Exclusividade de trilha de audio.
 *
 * O requisito e "duas gravacoes nunca tocam ao mesmo tempo". A tentacao e
 * resolver isso com `document.querySelectorAll('audio')` no clique do botao, e
 * essa solucao esta errada por tres motivos concretos:
 *
 *  1. varre o documento inteiro, entao alcanca audios de outras partes da
 *     aplicacao que nao deveriam ser controlados por esta lista;
 *  2. quebra com renderizacao condicional e com virtualizacao de lista -- o
 *     elemento que estava tocando pode ter saido do DOM (ou nunca ter entrado)
 *     e o seletor simplesmente nao o encontra;
 *  3. so roda no caminho do NOSSO botao. Play disparado pelos controles nativos
 *     do elemento, pelas teclas de midia do sistema operacional ou por qualquer
 *     chamada programatica passa ao largo.
 *
 * A solucao estrutural e um registro explicito: cada player se inscreve ao
 * montar, se desinscreve ao desmontar, e a exclusividade e imposta no momento
 * em que o ELEMENTO avisa que comecou a tocar (evento 'play'), nao no clique.
 * Todo caminho possivel de reproducao passa por ali.
 *
 * O registro vive num ref, e nao em estado: adicionar um player a lista nao e
 * informacao de renderizacao e nao deve custar um re-render na lista inteira de
 * campanhas. So `playingId` e estado, porque a interface precisa dele.
 */

interface AudioPlaybackContextValue {
  /** Id do player que esta tocando agora, ou null. */
  readonly playingId: string | null;
  /**
   * Inscreve um elemento. Devolve a funcao de baixa, que o chamador PRECISA
   * executar na limpeza do efeito -- registro orfao segura uma referencia a um
   * elemento fora do DOM e faria `claim` tentar pausar um fantasma.
   */
  readonly register: (id: string, element: HTMLAudioElement) => () => void;
  /** Declara que este id comecou a tocar: pausa todos os outros. */
  readonly claim: (id: string) => void;
  /** Declara que este id parou. Ignorado se outro ja assumiu a vez. */
  readonly release: (id: string) => void;
}

const AudioPlaybackContext = createContext<AudioPlaybackContextValue | null>(null);

interface AudioPlayerProviderProps {
  readonly children: ReactNode;
}

export function AudioPlayerProvider({ children }: AudioPlayerProviderProps): JSX.Element {
  const registroRef = useRef<Map<string, HTMLAudioElement> | null>(null);
  const [playingId, setPlayingId] = useState<string | null>(null);

  // Inicializacao preguicosa: `useRef(new Map())` alocaria um Map descartado a
  // cada render.
  const obterRegistro = useCallback((): Map<string, HTMLAudioElement> => {
    registroRef.current ??= new Map<string, HTMLAudioElement>();
    return registroRef.current;
  }, []);

  const register = useCallback(
    (id: string, element: HTMLAudioElement): (() => void) => {
      obterRegistro().set(id, element);
      return () => {
        obterRegistro().delete(id);
        // Se quem saiu era o que tocava, o estado precisa acompanhar; se nao
        // era, a atualizacao funcional evita pisar em quem assumiu depois.
        setPlayingId((atual) => (atual === id ? null : atual));
      };
    },
    [obterRegistro],
  );

  const claim = useCallback(
    (id: string): void => {
      for (const [outroId, elemento] of obterRegistro()) {
        if (outroId !== id && !elemento.paused) {
          // `pause()` dispara o evento 'pause' do outro player de forma
          // assincrona. O handler de la usa atualizacao funcional e so limpa o
          // estado se ainda for a vez dele -- quando a notificacao chegar,
          // `playingId` ja sera `id` e nada sera desfeito.
          elemento.pause();
        }
      }
      setPlayingId(id);
    },
    [obterRegistro],
  );

  const release = useCallback((id: string): void => {
    setPlayingId((atual) => (atual === id ? null : atual));
  }, []);

  const value = useMemo<AudioPlaybackContextValue>(
    () => ({ playingId, register, claim, release }),
    [playingId, register, claim, release],
  );

  return (
    <AudioPlaybackContext.Provider value={value}>{children}</AudioPlaybackContext.Provider>
  );
}

/**
 * Acesso ao registro de reproducao.
 *
 * Lanca fora do provedor de proposito: um player sem provedor continuaria
 * tocando normalmente e a violacao do RNF17 so apareceria quando alguem clicasse
 * em duas gravacoes: falha silenciosa, descoberta em producao.
 */
export function useAudioPlayback(): AudioPlaybackContextValue {
  const contexto = useContext(AudioPlaybackContext);
  if (contexto === null) {
    throw new Error(
      'useAudioPlayback exige <AudioPlayerProvider> acima na arvore (RNF17).',
    );
  }
  return contexto;
}
